import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';
import Account from '@material-ui/icons/AccountCircleOutlined';
import AssignmentInd from '@material-ui/icons/AssignmentIndOutlined';
import Person from '@material-ui/icons/PersonOutlined';
import VerticalAlignBottom from '@material-ui/icons/VerticalAlignBottom';
import Visibility from '@material-ui/icons/Visibility';
import Edit from '@material-ui/icons/Edit';
import Add from '@material-ui/icons/Add';
import Business from '@material-ui/icons/Business';
import CustomLink from 'components/CustomLink/CustomLink';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import AccountFilterTable from 'views/common/AccountFilterTable';
import Button from 'components/CustomButtons/Button';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import AddUserDialog from 'views/Users/common/AddUserDialog';
import FileUpload from 'components/CustomUpload/FileUpload';
import CsvSummaryDialog from 'views/Users/UsersList/subcomponents/CsvSummaryDialog';
import { AccountCell, DepartmentsCell, LinkCell, TextCell } from 'components/Cells/Cells';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import CanPerform from 'components/CanPerform/CanPerform';
import UserInfoDialog from 'views/Users/UsersList/subcomponents/UserInfoDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/usersStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { capitalize } from 'utils/converters/capitalize';
import { throwError } from 'rxjs';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { concatMap, finalize } from 'rxjs/operators';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import { UsersService } from 'services/users/users';
import { exportToXLS } from 'utils/export/export';
import invalidateCache, { InvalidateArray } from 'utils/api/invalidateCache';
import { setToast } from 'redux/modules/toast/actions';
import UploadFileDialog from 'components/UploadFileDialog/UploadFileDialog';
import ConfirmationDialogBody from 'views/Users/UsersList/subcomponents/ConfirmationDialogBody';
import AddUserCard from 'views/Users/UsersList/subcomponents/AddUserCard';
import { BodyM, HeadingSBold } from 'components/Typography/Typography';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { IFilter } from 'components/CustomFilter/CustomFilter';
import { Column } from 'react-table';
import { ImportedUsersData } from 'utils/api/models/users/importedUsersData';
import UserIcon from 'components/UserIcon/UserIcon';
import CustomButton from 'components/CustomButtons/Button';
import EditUserDialog from './subcomponents/EditUserDialog';
import EditUserConfirmationDialog from './subcomponents/EditUserConfirmationDialog';
import AddPermission from '../Details/UserDetails/dialogs/AddPermission';
import AddEmailDialog from './subcomponents/AddEmailDialog';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import { userData } from 'redux/modules/session/selectors';
import { MANAGER_TYPE } from '../constants';
import { getLanguage } from 'utils/language/language';
import { MAX_ADMIN_COUNT } from 'utils/const/users';

// @ts-ignore
const useStyles = makeStyles(styles);

const SAMPLE_CSV_URL_CORPORATE = '/download/ebu_import_sample_corporate.csv';
const SAMPLE_CSV_URL_ENTERPRISE = '/download/ebu_import_sample_enterprise.csv';

export enum UserType {
	SUPERADMIN = 'SUPERADMIN',
	ADMIN = 'ADMIN',
	MANAGER = 'MANAGER',
	ENDUSER = 'ENDUSER',
}

interface FormData {
	newEmail: string;
}

const dialogs = {
	NONE: 'none',
	ADD_USER_DIALOG: 'addUserDialog',
	UPLOAD_CSV_DIALOG: 'uploadCsvDialog',
	CSV_SUMMARY_DIALOG: 'csvSummaryDialog',
	USER_INFO_DIALOG: 'userInfoDialog',
	EDIT_DIALOG: 'editDialog',
	ADD_PERMISSIONS: 'addPermissions',
	ADD_EMAIL_DIALOG: 'addEmailDialog',
};

const UsersList = () => {
	const classes = useStyles();
	const [users, setUsers] = useState<SingleUser[]>([]);
	const [selected, setSelected] = useState<number[]>([]);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [filteredUsers, setFilteredUsers] = useState<SingleUser[]>([]);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<IAlternativeSelectItem | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
	const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [openDialog, setOpenDialog] = useState<string>(dialogs.NONE);
	const [uploadingCsv, setUploadingCsv] = useState<boolean>(false);
	const [uploadingCsvError, setUploadingCsvError] = useState<string>('');
	const [userType, setUserType] = useState<types.users.userType | null>(null);
	const [csvSummary, setCsvSummary] = useState<ImportedUsersData | null>(null);
	const [editedUserId, setEditedUserId] = useState<string>('');
	const [selectedUserType, setSelectedUserType] = useState<UserType>(UserType.ENDUSER);
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
	const [changeUserResults, setChangeUserResults] = useState<types.users.SingleChangeUserResult[]>([]);
	const dispatch = useDispatch();
	const currentUser = useSelector(userData);
	const pathName = `/dashboard/users`;
	const [filters, setFilters] = useState({
		userType: [],
		department: [],
	});
	const [isUserTypeChanged, setIsUserTypeChanged] = useState<boolean>(false);
	const [originalUserType, setOriginalUserType] = useState<string>('');
	const [formData, setFormData] = useState<FormData>({
		newEmail: '',
	});
	const [editedUserName, setEditedUserName] = useState('');
	const [editedUserAccountId, setEditedUserAccountId] = useState<string | undefined>('');

	const isEnterprise: boolean = accounts.length > 1;
	const sampleCsvUrl: string = isEnterprise ? SAMPLE_CSV_URL_ENTERPRISE : SAMPLE_CSV_URL_CORPORATE;
	const language = getLanguage();
	const hideDialog = () => {
		setOpenDialog(dialogs.NONE);
	};

	const showAddUserDialog = () => {
		setOpenDialog(dialogs.ADD_USER_DIALOG);
	};

	const showUploadCsvDialog = () => {
		setOpenDialog(dialogs.UPLOAD_CSV_DIALOG);
	};

	const showCsvSummaryDialog = () => {
		setOpenDialog(dialogs.CSV_SUMMARY_DIALOG);
	};

	const showUserInfoDialog = () => {
		setOpenDialog(dialogs.USER_INFO_DIALOG);
	};

	const showEditDialog = () => {
		setOpenDialog(dialogs.EDIT_DIALOG);
	};

	const showAddPermissionsDialog = () => {
		setOpenDialog(dialogs.ADD_PERMISSIONS);
	};

	const showAddEmailDialog = () => {
		setOpenDialog(dialogs.ADD_EMAIL_DIALOG);
	};

	const fetchUsers = (isChanged: boolean) => {
		setLoadingUsers(true);
		UsersService.getCorpUsersForAccounts()
			.pipe(finalize(() => setLoadingUsers(false)))
			.subscribe(
				({ users: fetchedUsers }) => {
					setUsers((users) => {
						const newUsers = _.differenceBy(fetchedUsers, users, 'userId');

						return [
							...users.map((user) => ({ ...user, new: false })),
							...newUsers.map((user) => ({ ...user, new: isChanged })),
						];
					});
				},
				(error) => throwError(error)
			);
	};

	const fetchAllUsers = () => {
		setLoadingUsers(true);
		UsersService.getCorpUsersForAccounts()
			.pipe(finalize(() => setLoadingUsers(false)))
			.subscribe(
				({ users: fetchedUsers }) => {
					setUsers(fetchedUsers);
				},
				(error) => throwError(error)
			);
	};

	const hasSelectedAllAccounts = !selectedAccount || (selectedAccount && selectedAccount.id === null);

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();
			const filtered = users.filter((user) => {
				return [
					[
						hasSelectedAllAccounts || selectedAccount?.id !== null,
						hasSelectedAllAccounts || user.accountId === selectedAccount?.id,
					],
					[filters.userType.length > 0, filters.userType.some((userType) => user.userType === userType)],
					[filters.department.length > 0, filters.department.some((deptId) => deptId === user.departmentId)],
					[
						_searchInputValue !== '',
						['userName', 'userType']
							.filter((field) => !!user[field])
							.some((field) => user[field].toLowerCase().includes(_searchInputValue)),
					],
				]
					.filter((check) => check[0])
					.every((check) => check[1]);
			});

			setFilteredUsers(filtered);
		}, 100),
		[searchInputValue, users, hasSelectedAllAccounts, filters, selectedAccount]
	);

	const userAddedSuccessfully = () => {
		invalidateCache(['getCorpAccountsWithAdminsForCurrentUser'], true);
		fetchUsers(true);
		fetchAccounts();
	};

	const userEditedSuccessfully = () => {
		invalidateCache(['getCorpAccountsWithAdminsForCurrentUser'], true);
		fetchAllUsers();
	};

	const exportToXls = useCallback(() => {
		const preparedData = (selected.length === 0
			? filteredUsers
			: selected.map((index) => filteredUsers[index])
		).map((user) => ({
			'User name': user.userName,
			'User type': user.userType,
			'Organization name': user.accountName,
			Department: user.departmentName ? user.departmentName : translate('numbers.details.notAssigned'),
		}));

		exportToXLS(preparedData, `users ${moment().format('LLL')}`);
	}, [selected, filteredUsers]);

	const onCsvUpload = (base64data: string) => {
		setUploadingCsv(true);
		UsersService.importUsersFromFile(base64data)
			.pipe(finalize(() => setUploadingCsv(false)))
			.subscribe(
				(response) => {
					invalidateCache(['getCorpUsersForAccounts'], true);
					fetchUsers(true);
					hideDialog();
					setCsvSummary(response);
					showCsvSummaryDialog();
				},
				(error) => {
					if (error.response.code === 'ERROR_RECOGNIZING_CSV_DELIMITER') {
						setUploadingCsvError(translate('users.incorrect.csv.format'));
					} else {
						setUploadingCsvError(translate('users.csv.something.went.wrong'));
					}
				}
			);
	};

	useEffect(() => {
		fetchUsers(false);
		return () => {
			dispatch(hideLoadingIndicator());
		};
		// eslint-disable-next-line
	}, [dispatch]);

	const fetchAccounts = () => {
		setLoadingAccounts(true);
		AccountsService.getCorpAccountsWithAdminsForCurrentUser()
			.pipe(finalize(() => setLoadingAccounts(false)))
			.subscribe(
				({ accounts }) => {
					setAccounts(accounts);
				},
				(error) => throwError(error)
			);
	};

	useEffect(() => {
		fetchAccounts();
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch]);

	useEffect(() => {
		setLoadingDepartments(true);
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => setLoadingDepartments(false)))
			.subscribe(
				({ departments }) => setDepartments(departments.filter((department) => department.deptId)),
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch]);

	useEffect(() => {
		if (!loadingDepartments && !loadingAccounts && !loadingUsers) {
			dispatch(hideLoadingIndicator());
			setLoading(false);
		} else {
			dispatch(showLoadingIndicator());
			setLoading(true);
		}
	}, [loadingUsers, loadingAccounts, loadingDepartments, dispatch]);

	useEffect(() => setSelected([]), [filteredUsers]);

	useEffect(() => {
		filter();
	}, [filters, filter, users, selectedAccount, hasSelectedAllAccounts, searchInputValue]);

	const userDetailsPath = (userId: string) => {
		return `${pathName}/${userId}`;
	};

	const onFilterChange = useCallback((name, value) => setFilters((filters) => ({ ...filters, [name]: value })), [
		setFilters,
	]);

	const selectedDepartmentName: string = useMemo(() => {
		return departments.find(({ deptId }) => deptId === selectedDepartmentId)?.name || '';
	}, [departments, selectedDepartmentId]);

	const accountDepartments = useMemo(() => {
		let departmentsList = departments
			.filter(({ accountId }) => !!selectedAccount && accountId === selectedAccount.id)
			.map((department) => ({
				title: department.name,
				value: department.deptId,
			}));
		// adding one more filter for unassigned departments
		return [...departmentsList, { title: translate('numbers.details.notAssigned'), value: '' }];
	}, [departments, selectedAccount, language]);

	const deleteUsers = () => {
		const userIds = _.chain(selected)
			.map((index) => filteredUsers[index])
			.filter(Boolean)
			.map((user) => user.userId)
			.value();
		dispatch(showLoadingIndicator());
		UsersService.deleteCorpUsers(userIds)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				({ response }) => {
					if (response.deleteUserResponse.result) {
						invalidateCache(['getCorpAccountsWithAdminsForCurrentUser'], true);
						setUsers((users) => {
							const filteredUsers = users.filter((user) => userIds.indexOf(user.userId) < 0);
							return filteredUsers.map((user) => ({ ...user, new: false }));
						});

						setSelected([]);
						dispatch(
							setToast({
								error: false,
								label: translate('users.deleted'),
								actionType: translate('deleted'),
							})
						);
						fetchAccounts();
					}
				},
				(error) => throwError(error)
			);
	};

	const addUser = (type: types.users.userType | null) => {
		setUserType(type);
		showAddUserDialog();
	};

	const resetEdit = () => {
		setEditedUserId('');
		setIsUserTypeChanged(false);
	};

	const changeUserTypeError = (code?: string) => {
		switch (code) {
			case 'MAXIMUM_ADMINS_LIMIT_EXCEEDED':
				return translate('error.maximum.admins.limit.exceeded');
			case 'EMAIL_NOT_FOUND':
				return translate('users.list.change.user.type.email.not.set');
			case 'ENDUSER_EMAIL_EXIST_AS_OTHER_ACCOUNT':
				return translate('users.list.change.user.type.email.exist.as.other.account');
			case 'DID_NOT_ADD_EMAIL':
				return translate('users.list.change.user.type.error.on.email.add');
			default:
				return translate('error.invalid.request');
		}
	};

	const handleChangeUserType = async () => {
		return new Promise<string>((resolve, reject) => {
			const cacheToInvalidate: InvalidateArray = [
				'getCorpAccountDetails',
				'getDepartmentsForCurrentUser',
				'getDepartmentDetails',
				'getCorpUsersForAccounts',
			];
			if (selectedUserType === UserType.ADMIN) {
				cacheToInvalidate.push('getCorpAccountAdmins');
			}
			if (selectedUserType === UserType.MANAGER) {
				cacheToInvalidate.push('getCorpAccountManagers');
			}
			UsersService.updateCorpUserType({
				userId: editedUserId,
				userType: selectedUserType,
			})
				.pipe(
					finalize(() => {
						dispatch(setToast({ active: false }));
					})
				)
				.subscribe(
					({ response }) => {
						if (response.updateCorpUserTypeResponse.result) {
							invalidateCache(cacheToInvalidate, true);
							resolve(
								translate(
									'users.details.userTypeChanged',
									translate(`role.${selectedUserType}`).toLowerCase()
								)
							);
						}
						reject(translate('error.invalid.request'));
					},
					(error) => {
						reject(changeUserTypeError(error?.response?.code));
					}
				);
		});
	};

	const handleChangeUserDepartment = async () => {
		return new Promise<string>((resolve, reject) => {
			const cacheToInvalidate: InvalidateArray = [
				'getCorpAccountDetails',
				'getDepartmentsForCurrentUser',
				'getDepartmentDetails',
				'getCorpUsersForAccounts',
			];
			if (selectedUserType === UserType.ADMIN) {
				cacheToInvalidate.push('getCorpAccountAdmins');
			}
			if (selectedUserType === UserType.MANAGER) {
				cacheToInvalidate.push('getCorpAccountManagers');
			}
			const updateCorpUserInformationResponse = UsersService.getCorpUserDetails(editedUserId).pipe(
				concatMap((user) => {
					const data: types.users.IEditData = {
						username: user.userData.userName,
						email: user.userData.email,
						departmentId: selectedDepartmentId,
						accountId: user.userData.accountId,
						contactNumber: user.userData.contactNumber1,
					};
					return UsersService.updateCorpUserInformation({
						userId: user.userData.userId,
						...data,
					});
				})
			);
			updateCorpUserInformationResponse
				.pipe(
					finalize(() => {
						dispatch(setToast({ active: false }));
					})
				)
				.subscribe(
					({ response }) => {
						if (response.updateCorpUserInformationResponse.result) {
							resolve(translate('users.details.userDepartmentChanged', selectedDepartmentName));
						}
					},
					() => {
						reject(translate('error.invalid.request'));
					}
				);
		});
	};

	const handleAddUserPermissions = (permissions: types.users.IExtendedSinglePermission[]) => {
		return new Promise<string>((resolve, reject) => {
			UsersService.updateCorpUserPermissions({ userId: editedUserId, override: true, permissions })
				.pipe(
					finalize(() => {
						dispatch(setToast({ active: false }));
					})
				)
				.subscribe(
					() => {
						resolve(translate('my.users.edit.permissions'));
					},
					() => {
						reject(translate('error.invalid.request'));
					}
				);
		});
	};

	const handleEditRequest = async (permissions?: types.users.IExtendedSinglePermission[]) => {
		const editedUser = users.find((user) => user.userId === editedUserId);
		if (editedUser) {
			const results: types.users.SingleChangeUserResult[] = [];
			dispatch(showLoadingIndicator());
			if (editedUser.userType !== selectedUserType) {
				try {
					const value = await handleChangeUserType();
					results.push({
						status: 'fulfilled',
						value,
					});
				} catch (value) {
					results.push({
						status: 'rejected',
						value: String(value),
					});
				}
			}
			if (editedUser.departmentId !== selectedDepartmentId) {
				try {
					const value = await handleChangeUserDepartment();
					results.push({
						status: 'fulfilled',
						value,
					});
				} catch (value) {
					results.push({
						status: 'rejected',
						value: String(value),
					});
				}
			}
			if (permissions?.length) {
				try {
					const value = await handleAddUserPermissions(permissions);
					results.push({
						status: 'fulfilled',
						value,
					});
				} catch (value) {
					results.push({
						status: 'rejected',
						value: String(value),
					});
				}
			}
			dispatch(hideLoadingIndicator());
			hideDialog();
			resetEdit();
			setChangeUserResults(results);
			userEditedSuccessfully();
		}
	};

	const pickDialogToOpen = (original: any, selectedUserType: UserType) => {
		UsersService.getCorpUserDetails(original.userId).subscribe(({ userData }) => {
			if (
				original.userType &&
				selectedUserType !== originalUserType &&
				originalUserType === UserType.ENDUSER &&
				!userData.email
			) {
				showAddEmailDialog();
			} else {
				showEditDialog();
			}
		});
	};

	const getAvailableUserTypes = (user: SingleUser) => {
		const availableUserTypes = [UserType.ENDUSER, UserType.MANAGER, UserType.ADMIN];

		if (user?.userType === UserType.ADMIN) return availableUserTypes;
		const userAccount = accounts?.find((account) => account?.accountName === user?.accountName);

		return userAccount && userAccount?.adminsCount >= MAX_ADMIN_COUNT
			? availableUserTypes.filter((type) => type !== UserType.ADMIN)
			: availableUserTypes;
	};

	const isFormValid = useMemo(() => {
		return !emailNotValid(formData.newEmail);
	}, [formData.newEmail]);

	const onChange = (name: keyof FormData, value: string) =>
		setFormData((formDataState) => ({ ...formDataState, [name]: value }));

	const onAddEmailSubmit = () => {
		UsersService.updateCorpUserInformation({
			userId: editedUserId,
			username: editedUserName,
			email: formData.newEmail,
			accountId: editedUserAccountId,
		}).subscribe(
			({ response }) => {
				if (response.updateCorpUserInformationResponse.result) {
					showEditDialog();
				}
			},
			(error) => {
				changeUserTypeError('DID_NOT_ADD_EMAIL');
			}
		);
	};
	return (
		<div style={{ width: '100%' }} className={classes.tableWrapper}>
			<EditUserDialog
				isDialogOpen={openDialog === dialogs.EDIT_DIALOG}
				hideDialog={hideDialog}
				resetEdit={resetEdit}
				handleEditRequest={handleEditRequest}
				isUserTypeChanged={isUserTypeChanged}
				userType={selectedUserType}
				previousUserType={originalUserType}
				showAddPermissionsDialog={showAddPermissionsDialog}
				accounts={accounts}
				departments={departments}
			/>
			<AddEmailDialog
				isDialogOpen={openDialog === dialogs.ADD_EMAIL_DIALOG}
				hideDialog={hideDialog}
				formData={formData}
				isValid={isFormValid}
				onChange={onChange}
				onSubmit={onAddEmailSubmit}
			/>
			<ConditionalRender
				show={openDialog === dialogs.ADD_PERMISSIONS}
				onTrue={() => (
					<AddPermission
						hideDialog={hideDialog}
						accounts={accounts.map((account) => ({
							id: account.accountId,
							name: account.accountName,
							adminsCount: account.adminsCount,
						}))}
						departments={departments}
						isEnterprise={isEnterprise}
						isManager={selectedUserType === UserType.MANAGER}
						isAdmin={selectedUserType === UserType.ADMIN}
						isCorporate={!isEnterprise}
						handleSaveData={(permissions) => handleEditRequest(permissions)}
					/>
				)}
			/>
			{users.length > 0 ? (
				<AccountFilterTable
					accounts={accounts}
					onInfoButtonClick={showUserInfoDialog}
					setAccount={(value) => setSelectedAccount(value)}
					selectedAccount={selectedAccount}
					onFilterChange={onFilterChange}
					filterHeader={translate('users.list')}
					filtersValue={filters}
					dropdown={{
						action: 'users:add-user',
						menu: (
							<Button color="tertiary-light" withIcon>
								<>
									<Add />
									{translate('add.user')}
								</>
							</Button>
						),
						items: [
							{
								text: translate('users.dropdown.add.new.user'),
								onClick: () => addUser(null),
							},
							{ text: translate('users.dropdown.import.from.file'), onClick: showUploadCsvDialog },
						],
					}}
					filters={
						[
							{
								title: translate('user.type'),
								name: 'userType',
								type: 'multiple',
								elements: ['ENDUSER', 'MANAGER', 'ADMIN', 'SUPERADMIN'].map((value) => ({
									title: translate(`numbers.user.${value}`),
									value,
								})),
							},
							!!selectedAccount &&
								selectedAccount?.id && {
									title: translate('numbers.department'),
									name: 'department',
									type: 'multiple',
									elements: accountDepartments,
								},
						].filter(Boolean) as IFilter[]
					}
					setSearchInputValue={(value) => setSearchInputValue(value)}
					searchInputValue={searchInputValue}
					exportToXLS={exportToXls}
					data={filteredUsers}
					onSelect={(value: any) => setSelected(value)}
					selectPanel={{
						selectedElements: selected.length,
						downloadMenuItems: [
							{
								name: 'XSLX',
								onClick: exportToXls,
							},
						],
						confirmationDialogTitle: translate('users.delete.users'),
						confirmationDialogBody: (
							<ConfirmationDialogBody allSelected={filteredUsers.length === selected.length} />
						),
						deleteItems: deleteUsers,
						deleteItemsAction: 'users:delete-users',
					}}
					loading={loading}
					selected={selected}
					columns={
						[
							{
								Header: translate('table.user.name'),
								// eslint-disable-next-line react/prop-types
								Cell: ({ original }: { original: SingleUser }) => (
									<LinkCell
										path={userDetailsPath(original.userId)}
										icon={<UserIcon type={original.userType as types.users.userType} />}
										text={original.userName}
									/>
								),
								minWidth: 250,
								accessor: 'userName',
							},
							{
								Header: translate('user.type'),
								Cell: ({ original }: { original: SingleUser }) =>
									editedUserId !== original.userId ? (
										<TextCell text={capitalize(original.userType)} />
									) : (
										<AlternativeSelect
											id="userType-select"
											value={
												<div className={classes.selectedItem}>
													<BodyM color="primary-light" disableMargin>
														{selectedUserType ? selectedUserType : original.userType}
													</BodyM>
												</div>
											}
											onClick={(item) => {
												setSelectedUserType(item);
												if (original.userType && item !== original.userType) {
													setIsUserTypeChanged(true);
													setOriginalUserType(original.userType);
												} else {
													setIsUserTypeChanged(false);
													setOriginalUserType('');
												}
											}}
											withHorizontalPadding
											className={classes.alternativeSelectUserType}
											items={getAvailableUserTypes(original)}
											selectedItems={selectedUserType ? [selectedUserType] : []}
										/>
									),
								minWidth: 140,
								accessor: 'userType',
							},
							hasSelectedAllAccounts && {
								Header: translate('table.organization.name'),
								Cell: ({ original }: { original: SingleUser }) => (
									<AccountCell
										text={original.accountName || ''}
										icon={
											<CustomAvatar
												accountId={original.accountId}
												alt="Company Logo"
												placeholderIcon={<Business />}
												customClasses={classes.inlineIcon}
											/>
										}
									/>
								),
								accessor: 'accountName',
								width: 250,
							},
							{
								Header: translate('numbers.department'),
								id: 'department',
								accessor: 'departmentName',
								width: 200,
								Cell: ({ original }: { original: SingleUser }) =>
									editedUserId !== original.userId ? (
										<DepartmentsCell
											departments={
												original.departmentId
													? ([
															{
																name: original.departmentName,
																uiColor: original.departmentUiColor,
																deptId: original.departmentId,
															},
													  ] as SingleDepartment[])
													: []
											}
										/>
									) : (
										<AlternativeSelect
											id="userDepartment-select"
											value={
												<div className={classes.selectedItem}>
													<BodyM color="primary-light" disableMargin>
														{selectedDepartmentName}
													</BodyM>
												</div>
											}
											onClick={(item) => {
												setSelectedDepartmentId(
													departments.find(({ deptId }) => deptId === item.id)?.deptId || ''
												);
											}}
											withHorizontalPadding
											className={classes.alternativeSelectDepartment}
											items={departments.map(({ deptId, name }) => ({
												id: deptId,
												name,
											}))}
											selectedItems={departments
												.filter(({ deptId }) => selectedDepartmentId === deptId)
												.map(({ name }) => name)}
										/>
									),
							},
							{
								id: 'edit',
								accessor: 'edit',
								filterable: false,
								sortable: false,
								resizable: false,
								width: 180,
								Cell: ({ original }: { original: SingleUser }) => (
									<div className={classes.buttonsWrapper}>
										{editedUserId !== original.userId ? (
											<CustomButton
												color="tertiary-light"
												onClick={() => {
													setEditedUserId(original.userId);
													setSelectedUserType(original.userType! as UserType);
													setSelectedDepartmentId(original.departmentId);
													setEditedUserName(original.userName);
													setEditedUserAccountId(original.accountId);
												}}
												disabled={currentUser.useType == MANAGER_TYPE}
											>
												{translate('edit')}
											</CustomButton>
										) : (
											<div>
												<CustomButton
													color="tertiary-light"
													onClick={() => pickDialogToOpen(original, selectedUserType)}
													disabled={
														selectedDepartmentId === original.departmentId &&
														selectedUserType === original.userType
													}
												>
													{translate('save')}
												</CustomButton>
												<CustomButton
													color="tertiary-danger"
													onClick={() => {
														setEditedUserId('');
														setEditedUserName('');
														setSelectedUserType(original.userType! as UserType);
														setIsUserTypeChanged(false);
													}}
												>
													{translate('cancel')}
												</CustomButton>
											</div>
										)}
									</div>
								),
							},
						].filter(Boolean) as Column<any>[]
					}
				/>
			) : (
				<GridContainer justify="flex-start">
					<ConditionalRender show={!loading}>
						<>
							<ConditionalRender show={accounts.length > 1}>
								<GridItem xs={12} sm={6} lg={3}>
									<AlternativeSelect
										label={translate('organization')}
										type="md"
										value={
											<div className={classes.selectedItem}>
												<CustomAvatar
													accountId={selectedAccount ? selectedAccount.id : null}
													alt="Company Logo"
													placeholderIcon={<Business />}
													customClasses={classes.selectedItemIcon}
												/>
												<HeadingSBold disableMargin>
													{selectedAccount
														? selectedAccount.name
														: translate('all.organizations')}
												</HeadingSBold>
											</div>
										}
										onClick={(account) => {
											if (selectedAccount && selectedAccount.id === account.id) {
												setSelectedAccount(null);
											} else {
												setSelectedAccount(account);
											}
										}}
										items={accounts.map((account) => ({
											name: account.accountName,
											id: account.accountId,
										}))}
										customIcons
										selectedItems={selectedAccount ? [selectedAccount.id] : []}
									/>
								</GridItem>
							</ConditionalRender>
							<CanPerform
								action="users:import-csv"
								yes={
									<GridItem xs={12}>
										<Card>
											<CardBody className={classes.csvImportWrapper}>
												<GridContainer>
													<GridItem xs={12} sm={6}>
														<div className={classes.csvUploadTexts}>
															<HeadingSBold>
																{translate('users.you.dont.have.users')}
															</HeadingSBold>
															<BodyM>{translate('users.you.can.upload.csv')}</BodyM>
															<CustomLink
																color="brand"
																to={sampleCsvUrl}
																type="bodyMBold"
																download
																target="_blank"
															>
																<div
																	className={classNames(
																		classes.downloadLink,
																		classes.leftAligned
																	)}
																>
																	<VerticalAlignBottom />
																	{translate('click.to.download.sample')}
																</div>
															</CustomLink>
														</div>
													</GridItem>
													<GridItem xs={12} sm={6}>
														<div className={classes.fileUploadWrapper}>
															<FileUpload
																text={translate('csv.drag.and.drop')}
																onFileChange={onCsvUpload}
																loadingText={translate('importing.users')}
																loading={uploadingCsv}
																errorMessage={uploadingCsvError}
																textSize="md"
															/>
														</div>
													</GridItem>
												</GridContainer>
											</CardBody>
										</Card>
									</GridItem>
								}
							/>
							<AddUserCard
								icon={<Account />}
								description={translate('end.user.description')}
								title={translate('numbers.user.ENDUSER')}
								listTitle={translate('end.user.no.access')}
								button={{
									text: translate('add.enduser'),
									onClick: () => addUser('ENDUSER'),
								}}
							/>
							<AddUserCard
								icon={<AssignmentInd />}
								description={translate('manager.description')}
								title={translate('numbers.user.MANAGER')}
								listTitle={translate('manager.can')}
								button={{
									text: translate('add.manager'),
									onClick: () => addUser('MANAGER'),
								}}
								listItems={[
									{
										icon: <Visibility />,
										text: translate('view.data'),
									},
									{
										icon: <VerticalAlignBottom />,
										text: translate('download.data'),
									},
								]}
							/>
							<CanPerform
								action="users:add-admin"
								yes={
									<AddUserCard
										icon={<Person />}
										description={translate('admin.description')}
										title={translate('numbers.user.ADMIN')}
										listTitle={translate('admin.can')}
										button={{
											text: translate('add.admin'),
											onClick: () => addUser('ADMIN'),
										}}
										listItems={[
											{
												icon: <Visibility />,
												text: translate('view.data'),
											},
											{
												icon: <VerticalAlignBottom />,
												text: translate('download.data'),
											},
											{
												icon: <Edit />,
												text: translate('edit.data'),
											},
											{
												icon: <Edit />,
												text: translate('create.users'),
											},
										]}
									/>
								}
							/>
						</>
					</ConditionalRender>
				</GridContainer>
			)}
			<AddUserDialog
				isDialogOpen={openDialog === dialogs.ADD_USER_DIALOG}
				setIsDialogOpen={(value) => (value ? showAddUserDialog() : hideDialog())}
				dialogHeader={userType ? translate(`add.${userType.toLowerCase()}`) : translate('add.user')}
				userType={userType || undefined}
				accounts={accounts}
				departments={departments}
				userAddedSuccessfully={userAddedSuccessfully}
				toastSuccessMessage={
					userType ? translate(`${userType.toLowerCase()}.user.added`) : translate('user.added')
				}
			/>
			<UploadFileDialog
				onFileChange={onCsvUpload}
				hideDialog={hideDialog}
				isOpen={openDialog === dialogs.UPLOAD_CSV_DIALOG}
				uploadingCsv={uploadingCsv}
				uploadingCsvError={uploadingCsvError}
				sampleCsvUrl={sampleCsvUrl}
				header={translate('users.dialog.import.from.file')}
			/>
			<CsvSummaryDialog
				isOpen={openDialog === dialogs.CSV_SUMMARY_DIALOG}
				hideDialog={hideDialog}
				summary={csvSummary}
			/>
			<UserInfoDialog hideDialog={hideDialog} isDialogOpen={openDialog === dialogs.USER_INFO_DIALOG} />
			<EditUserConfirmationDialog
				changeUserResults={changeUserResults}
				hideDialog={() => setChangeUserResults([])}
			/>
		</div>
	);
};

export default UsersList;
